/**
 * cohortDocTiles — Personal cohort → Stage 4 doc-tile spec generator.
 *
 * Drives PdwShell's Stage 4 'docs-grid' tile set per the canonical SPEC
 * Screen 12 + Bill's architecture lock memo (project_pdw_architecture_lock_
 * 20260514): Stage 4 is the curated "Documents to capture" landing the FSR
 * sees BEFORE the Stage 9 review form opens — captured docs auto-fill the
 * downstream identity / address / SSN-TIN fields.
 *
 * v0.14.50 — BACKLOG-0072 (Bill 2026-05-26). First Personal-cohort wiring;
 * non-Personal subtype tile sets (Business Articles, Trust Cert, Court
 * docs, etc.) land in subsequent batches when those buckets come into the
 * shell. NRA visa-class-specific branch tiles re-use the canonical set
 * defined by the existing IndividualNraDocumentScanTabs / nraDocPath state
 * machine — see SPEC Screen 12 § "NRA document set" + memory
 * `feedback_no_hallucinated_fields_without_core_director_destination_
 * 20260512` for source citations on each required doc.
 *
 * The OPTIONAL SSN Card tile is intentionally rendered for every cohort
 * (not required for any cohort but it pre-fills the SSN-TIN tab when
 * captured).
 *
 * LOCKED v6 contract (Bill 2026-06-11) — docs/operations/person-modal-
 * inventory/06-INDIVIDUAL-SURFACE-CLEANUP-MOCKUP.html (+ 03-DETERMINISTIC-
 * TILES-DECISION.md, 00-NORTH-STAR-BRIEF.md D4/D6) is the source for two
 * changes in this file:
 *   1. Photo-ID always an option — every nra-* cohort now offers the
 *      OPTIONAL generic 'secondary-id' tile (label "Driver's License /
 *      State ID"). The CIP slot machine (assignCipSlots, admin-config F254
 *      priority) re-ranks on capture: a captured DL (priority 1) outranks
 *      the passport (priority 3) for CIP ID-1, bumping the passport to
 *      ID-2 automatically.
 *   2. 'address-proof' removed from the shared optional set — Address
 *      Verification relocates beside the Address & Contact fields it
 *      corroborates (rendered there by the individual surface using the
 *      SAME 'address-proof' tileId; that identifier is a cross-lane
 *      contract — do not rename).
 *
 * Bill ruling 2026-06-12 (citizenship-path sanity check, ruling #2):
 * permanent-resident 'primary-id' downgraded REQUIRED → OPTIONAL — the Green
 * Card IS a government photo ID; requiring a second photo ID exceeds CIP and
 * has no procedure citation. Green Card (I-551) stays REQUIRED. A second
 * photo ID is not required but encouraged if provided (especially a DL — the
 * CIP slot machine re-ranks it to ID-1 automatically).
 *
 * SINGLE SOURCE OF TRUTH: this module owns tile label / hint / gating for
 * the Personal-cohort docs grid. No render layer may substitute or transform
 * these specs (PdwStage4DocsGrid renders them verbatim; the only permitted
 * post-capture override is the generic-slot adopt-on-classify branding).
 */

import type { RaoPerson } from '../../types';

/** Stable cohort key driving the tile set. Derived from citizenshipStatus + stayPurpose. */
export type PersonalCohortKey =
  | 'us-citizen'
  | 'permanent-resident'
  | 'nra-work'
  | 'nra-student'
  | 'nra-exchange'
  | 'nra-visitor';

/** Required/Optional gating per canonical SPEC + WizardDocMatrix. */
export type CohortTileGating = 'REQUIRED' | 'OPTIONAL';

export interface CohortDocTileSpec {
  /**
   * Stable slug — MUST be a canonical doc-type value from
   * `captureRouting.DOC_TYPE_OPTIONS` (BACKLOG-0600 / punch-list S5.1): the
   * tile id is the routing key handed to `routeCapture`, which maps it to the
   * extraction backend + `/v3/nra/scan` wire id. Pre-0600 this file used raw
   * 'passport' / 'visa' ids that findDocType couldn't resolve → captures fell
   * to the 'other' attach-only bucket with NO extraction. Legacy ids are still
   * tolerated at the boundary via `normalizeDocTypeValue`.
   */
  tileId: string;
  label: string;
  hint: string;
  /** DOCUMENT_ICONS slug used by DocsToCaptureGrid for tile thumbnails. */
  iconSlug: string;
  gatingLevel: CohortTileGating;
  /**
   * slot-1 T4 — manual docs that have NO trained Azure DI extractor. The
   * capture flow attaches the file (preflight + PDF-save) but skips the DI
   * pass. The H-1B I-797 USCIS approval notice + the visa-class proof bucket
   * (intracompany letter / O-1 petition / TN offer) are manual. (i797b is
   * dropped — TR is deleting the dead classifier refs; H-1B keeps plain i797.)
   */
  noExtraction?: boolean;
}

/**
 * Compute the cohort key from a seeded Personal draft. Citizenship cohort
 * gate writes both citizenshipStatus + extra.stayPurpose (NRA path), so
 * this reads those + maps to the canonical PersonalCohortKey.
 */
export function deriveCohortKey(person: RaoPerson): PersonalCohortKey {
  const status = person.citizenshipStatus;
  if (status === 'Permanent Resident') return 'permanent-resident';
  if (status === 'Non-Resident Alien') {
    const stayPurpose = (person.extra as { stayPurpose?: string } | undefined)?.stayPurpose;
    switch (stayPurpose) {
      case 'Work':              return 'nra-work';
      case 'Student':           return 'nra-student';
      case 'Exchange Visitor':  return 'nra-exchange';
      // Bill ruling 2026-06-12 — Visitor/Tourist is NOT eligible ("Legacy
      // Procedure - Non-Citizen ID Requirements.txt": Non-Acceptable Visas
      // B / VWP). New adds never reach tile derivation with this purpose
      // (IndividualReviewBody.cohortReady excludes it); the key + tile set
      // below are retained ONLY for legacy/hydrated drafts.
      case 'Visitor / Tourist': return 'nra-visitor';
      // ⚠ Silent-fallback caveat (2026-06-12 divergence investigation): any
      // UNMATCHED stayPurpose string renders the WORK tile set — including
      // the Work-specific us-visa hint — with no visual indication. The four
      // literals above are the only values the purpose cards write today;
      // legacy/hydrated values outside that set land here.
      default:                  return 'nra-work'; // safe default
    }
  }
  // 'US Citizen' + any unmapped status → us-citizen
  return 'us-citizen';
}

/**
 * Tile set per cohort. Required-set is canonical per SPEC; Optional set
 * (SSN Card) is universal. Locked v6 (Bill 2026-06-11): 'address-proof' is
 * NOT in this grid — Address Verification renders beside the Address &
 * Contact fields it corroborates (same 'address-proof' tileId, owned by
 * the individual review surface).
 */
export function cohortDocTiles(cohort: PersonalCohortKey): ReadonlyArray<CohortDocTileSpec> {
  const optional: ReadonlyArray<CohortDocTileSpec> = [
    {
      tileId: 'ssn-card',
      label: 'SSN Card',
      hint: 'Optional — auto-fills SSN-TIN tab when captured.',
      iconSlug: 'ssn-card',
      gatingLevel: 'OPTIONAL',
    },
    // NRA smoke 2026-06-12 — supplemental catch-all. Live gap: customer
    // presented a student photo ID with no tile to receive it. tileId 'other'
    // is the canonical DOC_TYPE_OPTIONS no-extraction value (captureRouting),
    // so the capture attaches to the customer file with NO DI/CU pass —
    // routing needs no change. NOT a CIP document: never feeds the slot
    // machine, never required.
    // Hint clause per "Legacy Procedure - Identification Forms.txt": school
    // identification cards and local company employee ID cards are classified
    // PRIMARY identification under PCB procedure.
    {
      tileId: 'other',
      label: 'Other supporting document',
      hint: 'Optional — supplemental items (e.g. a student or employee photo ID — classified primary identification per PCB procedure — enrollment or relationship letters). Attaches to the customer file without auto-extraction.',
      iconSlug: 'tax-form',
      gatingLevel: 'OPTIONAL',
      noExtraction: true,
    },
  ];

  // Locked v6 (Bill 2026-06-11) — photo-ID always an option: every nra-*
  // cohort offers this OPTIONAL generic secondary-id tile (same slot-machine
  // mechanics as the us-citizen / permanent-resident secondary-id tiles —
  // PdwStage4DocsGrid gives generic slots the F254 Classify Picker +
  // auto-classify, and assignCipSlots re-ranks captures by admin-config F254
  // priority, so a captured DL claims CIP ID-1 and bumps the passport to ID-2).
  const nraPhotoId: CohortDocTileSpec = {
    tileId: 'secondary-id',
    label: 'Driver\'s License / State ID',
    hint: 'Optional — if captured, DL outranks the passport for CIP ID-1 (priority 1 vs 3); the passport bumps to ID-2 automatically.',
    iconSlug: 'drivers-license',
    gatingLevel: 'OPTIONAL',
  };

  // BACKLOG-0762 — OPTIONAL EAD (I-766) tile on every eligible nra-* cohort.
  // Procedure source: "Legacy Procedure - Non-Citizen ID Requirements.txt" —
  // the Employment Authorization Card is listed as an accepted alternative
  // alongside the visa ("Visa … or Employment Authorization Card") and the
  // I-94 ("May not have this if they have an Employment Authorization Card").
  // EXTRACTABLE: tileId 'ead' is the canonical DOC_TYPE_OPTIONS value —
  // routeCapture sends it down the /v3/nra/scan path (nraType 'ead', trained
  // field family eadANumber / eadCategory / eadExpDt; captureRouting 'ead'
  // case), canonical Stage-9 DocCode 'EAD' via tileIdToCanonicalDocCode. Not
  // a CIP document (no F254 code) — never feeds the slot machine.
  // Also the work-eligibility proof the Employment section's J-2 soft gate
  // looks for (IndividualEmploymentSection.hasCapturedEad).
  const nraEad: CohortDocTileSpec = {
    tileId: 'ead',
    label: 'Employment Authorization (EAD)',
    hint: 'Optional — I-766 work permit. Accepted alternative to the visa / I-94 per PCB procedure; for J-2 dependents it is the proof needed before listing Employed.',
    iconSlug: 'ead',
    gatingLevel: 'OPTIONAL',
  };

  switch (cohort) {
    case 'us-citizen':
      return [
        {
          tileId: 'primary-id',
          label: 'Primary Photo ID',
          hint: 'Auto-detects Driver\'s License / State ID / US Passport.',
          iconSlug: 'drivers-license',
          gatingLevel: 'REQUIRED',
        },
        // Step 2 — generic Secondary Photo ID slot. Optional for US Citizen
        // (one ID satisfies CIP); accepts ANY photo ID, F254 priority decides
        // Primary↔Secondary ordering at capture time via slot-machine logic.
        {
          tileId: 'secondary-id',
          label: 'Secondary Photo ID',
          hint: 'Optional — any additional photo ID strengthens CIP verification.',
          iconSlug: 'drivers-license',
          gatingLevel: 'OPTIONAL',
        },
        ...optional,
      ];

    case 'permanent-resident':
      return [
        {
          tileId: 'green-card',
          label: 'Green Card (I-551)',
          hint: 'Permanent Resident Card — primary CIP + lawful-presence proof.',
          iconSlug: 'green-card',
          gatingLevel: 'REQUIRED',
        },
        // Bill ruling 2026-06-12 (#2) — REQUIRED → OPTIONAL: the Green Card
        // IS a government photo ID; a second photo ID exceeds CIP (no
        // procedure citation). Encouraged if provided — a DL outranks the
        // Green Card for CIP ID-1 via the slot machine (F254 1 vs 3).
        {
          tileId: 'primary-id',
          label: 'Primary Photo ID',
          hint: 'Not required — encouraged if provided (especially a Driver\'s License).',
          iconSlug: 'drivers-license',
          gatingLevel: 'OPTIONAL',
        },
        // Step 2 — generic Secondary Photo ID slot for PR cohort. Optional
        // (Green Card + Primary ID together satisfy lawful-presence + CIP);
        // accepts ANY photo ID.
        {
          tileId: 'secondary-id',
          label: 'Secondary Photo ID',
          hint: 'Optional — any additional photo ID strengthens CIP verification.',
          iconSlug: 'drivers-license',
          gatingLevel: 'OPTIONAL',
        },
        ...optional,
      ];

    case 'nra-work':
      return [
        { tileId: 'foreign-passport',         label: 'Foreign Passport',  hint: 'Photo data page (ICAO 9303 MRZ parse). Primary CIP.', iconSlug: 'foreign-passport', gatingLevel: 'REQUIRED' },
        { tileId: 'us-visa',             label: 'US Visa',           hint: 'H-1B / L-1 / O-1 / TN / E-1/2/3 visa stamp.',          iconSlug: 'visa',             gatingLevel: 'REQUIRED' },
        { tileId: 'i94',              label: 'I-94',              hint: 'Most recent CBP I-94 admission record.',                iconSlug: 'i94',              gatingLevel: 'REQUIRED' },
        // slot-1 T4 — I-797 USCIS approval notice, folded in from the NRA
        // scanner tile set as a MANUAL (no-extraction) tile. i797b dropped.
        // Bill 2026-06-12 (sanity-check) — relabeled off "(H-1B)": I-797
        // approval notices exist for the petition-based classes (H / L / O),
        // not TN/E; stays OPTIONAL so it is satisfiable for TN/E workers.
        { tileId: 'i797',             label: 'I-797 approval notice', hint: 'USCIS approval notice — petition-based classes (H / L / O). Manual attach — no auto-extraction.', iconSlug: 'i797', gatingLevel: 'OPTIONAL', noExtraction: true },
        { tileId: 'visa-class-proof', label: 'Visa-class proof',  hint: 'Intracompany letter (L-1) / O-1 petition / TN offer. Manual attach — no auto-extraction.', iconSlug: 'tax-form', gatingLevel: 'REQUIRED', noExtraction: true },
        nraPhotoId,
        nraEad,
        ...optional,
      ];

    case 'nra-student':
      return [
        { tileId: 'foreign-passport', label: 'Foreign Passport', hint: 'Photo data page (ICAO 9303 MRZ parse). Primary CIP.', iconSlug: 'foreign-passport', gatingLevel: 'REQUIRED' },
        { tileId: 'us-visa',     label: 'US Visa',          hint: 'F-1 / F-2 / M-1 student visa stamp.',                  iconSlug: 'visa',             gatingLevel: 'REQUIRED' },
        { tileId: 'i94',      label: 'I-94',             hint: 'Most recent CBP I-94 admission record.',                iconSlug: 'i94',              gatingLevel: 'REQUIRED' },
        { tileId: 'i20',      label: 'I-20',             hint: 'DHS/ICE I-20 — school enrollment proof.',               iconSlug: 'i20',              gatingLevel: 'REQUIRED' },
        nraPhotoId,
        nraEad,
        ...optional,
      ];

    case 'nra-exchange':
      return [
        { tileId: 'foreign-passport', label: 'Foreign Passport', hint: 'Photo data page (ICAO 9303 MRZ parse). Primary CIP.',          iconSlug: 'foreign-passport', gatingLevel: 'REQUIRED' },
        { tileId: 'us-visa',     label: 'US Visa',          hint: 'J-1 / J-2 exchange-visitor visa stamp.',                        iconSlug: 'visa',             gatingLevel: 'REQUIRED' },
        { tileId: 'i94',      label: 'I-94',             hint: 'Most recent CBP I-94 admission record.',                        iconSlug: 'i94',              gatingLevel: 'REQUIRED' },
        { tileId: 'ds2019',   label: 'DS-2019',          hint: 'DOS Certificate of Eligibility for Exchange Visitor Status.',   iconSlug: 'ds2019',           gatingLevel: 'REQUIRED' },
        nraPhotoId,
        // J-2 dependents in this cohort: the EAD is the work-eligibility
        // proof the Employment section's soft gate waits for.
        nraEad,
        ...optional,
      ];

    case 'nra-visitor':
      return [
        { tileId: 'foreign-passport', label: 'Foreign Passport', hint: 'Photo data page (ICAO 9303 MRZ parse). Primary CIP.',  iconSlug: 'foreign-passport', gatingLevel: 'REQUIRED' },
        { tileId: 'us-visa',     label: 'US Visa',          hint: 'B-1 / B-2 visitor visa (VWP / ESTA also acceptable).', iconSlug: 'visa',             gatingLevel: 'REQUIRED' },
        { tileId: 'i94',      label: 'I-94',             hint: 'Most recent CBP I-94 admission record.',                iconSlug: 'i94',              gatingLevel: 'REQUIRED' },
        nraPhotoId,
        ...optional,
      ];
  }
}

/**
 * Check whether all REQUIRED tiles in a cohort's tile list are captured.
 * Caller passes the set of captured tile IDs; the gate is "all required ⊆
 * captured". Used by PdwShell's docs-grid Continue button.
 */
export function allRequiredCaptured(
  cohort: PersonalCohortKey,
  capturedTileIds: ReadonlySet<string>,
): boolean {
  const required = cohortDocTiles(cohort).filter((t) => t.gatingLevel === 'REQUIRED');
  return required.every((t) => capturedTileIds.has(t.tileId));
}
